import { useState, useEffect, useCallback } from 'react';
import { getNodes, getNode, getNodeHistory } from '@/services/api';
import type { NodeSummary, NodeDetail, StateEvent, NodeHistory, ProductTier } from '@/types/node';
import type { Period } from '@/types/metrics';
import { useDashboardStore } from '@/stores/useDashboardStore';

export function useNodes(params?: {
  portfolio?: string;
  state?: string;
  profile?: string;
  search?: string;
  softwareVersion?: string;
  vendor?: string;
  lifecycle?: string;
  tier?: ProductTier;
  commissionedWithinDays?: number;
  page?: number;
  limit?: number;
  sortBy?: string;
  sortOrder?: 'asc' | 'desc';
}) {
  const refreshKey = useDashboardStore((state) => state.refreshKey);
  const [nodes, setNodes] = useState<NodeSummary[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  const fetchNodes = useCallback(async () => {
    try {
      setLoading(true);
      const response = await getNodes(params);
      setNodes(response.nodes);
      setTotal(response.pagination.total);
      setError(null);
    } catch (err) {
      setError(err instanceof Error ? err : new Error('Failed to fetch nodes'));
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    params?.portfolio,
    params?.state,
    params?.profile,
    params?.search,
    params?.softwareVersion,
    params?.vendor,
    params?.lifecycle,
    params?.tier,
    params?.commissionedWithinDays,
    params?.page,
    params?.limit,
    params?.sortBy,
    params?.sortOrder,
    refreshKey,
  ]);

  useEffect(() => {
    fetchNodes();
  }, [fetchNodes]);

  return { nodes, total, loading, error, refetch: fetchNodes };
}

export function useNode(id: string) {
  const refreshKey = useDashboardStore((state) => state.refreshKey);
  const [node, setNode] = useState<NodeDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  useEffect(() => {
    async function fetch() {
      try {
        setLoading(true);
        const response = await getNode(id);
        setNode(response.node);
        setError(null);
      } catch (err) {
        setError(err instanceof Error ? err : new Error('Failed to fetch node'));
      } finally {
        setLoading(false);
      }
    }
    fetch();
  }, [id, refreshKey]);

  return { node, loading, error };
}

export function useNodeHistory(id: string, period: Period = '24h') {
  const refreshKey = useDashboardStore((state) => state.refreshKey);
  const [history, setHistory] = useState<NodeHistory[]>([]);
  const [events, setEvents] = useState<StateEvent[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  useEffect(() => {
    async function fetch() {
      try {
        setLoading(true);
        const response = await getNodeHistory(id, period);
        setHistory(response.history);
        setEvents(response.events);
        setError(null);
      } catch (err) {
        setError(err instanceof Error ? err : new Error('Failed to fetch node history'));
      } finally {
        setLoading(false);
      }
    }
    fetch();
  }, [id, period, refreshKey]);

  return { history, events, loading, error };
}
